import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRegistryPerms } from '../lib/perms'
import { Plus, Trash2, Pencil, MapPin, Layers, Building2, Image as ImageIcon } from 'lucide-react'
import MineWizard from './MineWizard'
import PitWizard from './PitWizard'
import { useConfirm, deleteJSON } from './ConfirmDialog'
import { ErrorState } from './ViewState'
import { pitStatusLabel, pitTypeLabel, oreTypeLabel } from '../equipment'
import { useWS } from '../contexts/WSContext'

const oreList = (m) => Array.isArray(m.ore_types) && m.ore_types.length ? m.ore_types : (m.ore_type ? [m.ore_type] : [])
const equipCount = (m) => Array.isArray(m.pits) ? m.pits.reduce((n, p) => n + (Array.isArray(p.equipment_ids) ? p.equipment_ids.length : 0), 0) : 0

// Administração › Minas: hierarquia Cliente › Mina › Pits › Equipamentos.
// Mina e Pit são cadastrados em wizards (modal central); pits vivem em tabela
// própria (/api/pits) e aparecem agregados dentro da mina.
export default function AdminMines() {
  const { t } = useTranslation()
  const { mayEdit, mayDelete, reasonEdit, reasonDelete } = useRegistryPerms()
  const { mines, clients, adminError, reloadAdmin } = useWS()
  const [mineOpen, setMineOpen] = useState(false)
  const [editingMine, setEditingMine] = useState(null)
  const [pitOpen, setPitOpen] = useState(false)
  const [editingPit, setEditingPit] = useState(null)
  const [pitMineId, setPitMineId] = useState(null)
  const [confirmDialog, ask] = useConfirm()

  const openCreateMine = () => { setEditingMine(null); setMineOpen(true) }
  const openEditMine = (m) => { setEditingMine(m); setMineOpen(true) }

  // Apagar uma mina cascateia os pits e as atribuições e zera o mine_id dos
  // equipamentos, então pede o nome digitado; o pit pede confirmação simples.
  const removeMine = (m) => ask({
    title: t('admin:mines.delete_title'),
    message: t('admin:mines.delete_message', { name: m.name, pits: Array.isArray(m.pits) ? m.pits.length : 0, equips: equipCount(m) }),
    confirmWord: m.name,
    confirmLabel: t('common:actions.delete'),
    danger: true,
    onConfirm: async () => { await deleteJSON(`/api/mines/${m.id}`); reloadAdmin() },
  })

  const openCreatePit = (m) => { setEditingPit(null); setPitMineId(m.id); setPitOpen(true) }
  const openEditPit = (m, p) => { setEditingPit({ ...p, mine_id: m.id }); setPitMineId(m.id); setPitOpen(true) }
  const removePit = (p) => ask({
    title: t('admin:mines.remove_pit'),
    message: t('admin:pits.confirm_delete', { name: p.name }),
    confirmLabel: t('common:actions.delete'),
    danger: true,
    onConfirm: async () => { await deleteJSON(`/api/pits/${p.id}`); reloadAdmin() },
  })

  const clientName = (m) => clients.find(c => c.id === m.client_id)?.name

  return (
    <div className="h-full overflow-y-auto p-6 scrollbar-thin">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('admin:mines.count', { count: mines.length })}</p>
        <button disabled={!mayEdit} title={!mayEdit ? t(reasonEdit) : undefined} onClick={openCreateMine} className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-lg bg-brand text-white hover:bg-brand-700 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus size={16} /> {t('admin:mines.add')}
        </button>
      </div>

      {adminError ? (
        <ErrorState message={adminError} onRetry={reloadAdmin} />
      ) : mines.length === 0 ? (
        <button disabled={!mayEdit} title={!mayEdit ? t(reasonEdit) : undefined} onClick={openCreateMine} className="w-full flex flex-col items-center justify-center gap-2 h-48 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:border-brand/50 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus size={28} />
          <span className="text-sm">{t('admin:mines.empty_cta')}</span>
        </button>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {mines.map(m => {
            const pits = Array.isArray(m.pits) ? m.pits : []
            const ores = oreList(m)
            const cName = clientName(m)
            return (
              <div key={m.id} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-md transition-shadow">
                {m.aerial_image_id && (
                  <img src={`/api/images/${m.aerial_image_id}`} alt="" className="w-full h-24 object-cover" />
                )}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm truncate">{m.name}</p>
                      <div className="mt-0.5 flex flex-wrap gap-1">
                        {ores.length ? ores.map(o => (
                          <span key={o} className="px-1.5 py-0.5 rounded text-[11px] bg-brand/10 text-brand-700 dark:text-brand-300 font-medium">{oreTypeLabel(o)}</span>
                        )) : <span className="text-xs text-slate-400 dark:text-slate-500">—</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button disabled={!mayEdit} onClick={() => openEditMine(m)} title={!mayEdit ? t(reasonEdit) : t('common:actions.edit')} className="text-slate-300 dark:text-slate-600 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed"><Pencil size={15} /></button>
                      <button disabled={!mayDelete} onClick={() => removeMine(m)} title={!mayDelete ? t(reasonDelete) : t('admin:actions.remove')} className="text-slate-300 dark:text-slate-600 hover:text-status-down disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 size={15} /></button>
                    </div>
                  </div>
                  <div className="mt-2 space-y-1 text-xs text-slate-500 dark:text-slate-400">
                    {cName && <p className="flex items-center gap-1.5"><Building2 size={12} className="text-slate-400 dark:text-slate-500" /> {cName}</p>}
                    {m.lat != null && m.lon != null && (
                      <p className="flex items-center gap-1.5"><MapPin size={12} className="text-slate-400 dark:text-slate-500" /> <span className="tabular-nums">{Number(m.lat).toFixed(4)}, {Number(m.lon).toFixed(4)}</span></p>
                    )}
                    <p className="flex items-center gap-1.5"><Layers size={12} className="text-slate-400 dark:text-slate-500" /> {t('admin:mines.pits_equips', { pits: pits.length, equips: equipCount(m) })}</p>
                  </div>

                  <div className="mt-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">{t('admin:mines.pits_label')}</span>
                      <button disabled={!mayEdit} title={!mayEdit ? t(reasonEdit) : undefined} onClick={() => openCreatePit(m)} className="text-xs text-brand-600 hover:text-brand-700 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                        <Plus size={12} /> {t('admin:mines.add_pit')}
                      </button>
                    </div>
                    {pits.length === 0 ? (
                      <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">{t('admin:mines.no_pits')}</p>
                    ) : (
                      <div className="mt-1.5 space-y-1">
                        {pits.map(p => (
                          <div key={p.id} className="flex items-center gap-2 rounded-md bg-slate-50 dark:bg-slate-800/60 px-2 py-1.5">
                            {p.image_url
                              ? <img src={p.image_url} alt="" className="w-7 h-7 rounded object-cover shrink-0" />
                              : <span className="w-7 h-7 rounded bg-slate-200 dark:bg-slate-700 flex items-center justify-center shrink-0"><ImageIcon size={12} className="text-slate-400" /></span>}
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate">{p.name}</p>
                              <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">
                                {[oreTypeLabel(p.ore_type), pitTypeLabel(p.type), pitStatusLabel(p.status)].filter(Boolean).join(' · ')} · {t('admin:mines.equip_count', { count: Array.isArray(p.equipment_ids) ? p.equipment_ids.length : 0 })}
                              </p>
                            </div>
                            <button disabled={!mayEdit} onClick={() => openEditPit(m, p)} title={!mayEdit ? t(reasonEdit) : t('admin:mines.edit_pit')} className="text-slate-300 dark:text-slate-600 hover:text-brand-600 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"><Pencil size={13} /></button>
                            <button disabled={!mayDelete} onClick={() => removePit(p)} title={!mayDelete ? t(reasonDelete) : t('admin:mines.remove_pit')} className="text-slate-300 dark:text-slate-600 hover:text-status-down shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 size={13} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <MineWizard key={`mine-${editingMine?.id ?? 'new'}-${mineOpen}`} open={mineOpen} onClose={() => setMineOpen(false)} editing={editingMine} />
      <PitWizard key={`pit-${editingPit?.id ?? 'new'}-${pitOpen}`} open={pitOpen} onClose={() => setPitOpen(false)} editing={editingPit} mineId={pitMineId} />
      {confirmDialog}
    </div>
  )
}
